import { cn } from '@/shared/lib/utils';

/** Compact header ops chip — light border, soft lift. */
export const HEADER_CHIP_CLASS = cn(
  'h-9 shrink-0 gap-1.5 rounded-xl border border-surface-200',
  'bg-white/80 px-2 py-0 shadow-soft',
  'text-[12px] font-semibold text-ink-700',
  'hover:border-surface-200 hover:bg-white hover:text-ink-900',
  'focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:ring-offset-1',
);

/** Icon-only chip (Tips). */
export const HEADER_CHIP_ICON_ONLY = cn(HEADER_CHIP_CLASS, 'w-9 justify-center px-0');

/** Icon well inside header chips. */
export const HEADER_CHIP_ICON_WELL = cn(
  'flex size-6 shrink-0 items-center justify-center rounded-lg',
  'bg-brand-50 text-brand-600',
);

/** Right-side tools — above header wash so brand CTAs stay vivid. */
export const HEADER_TOOLBAR_CLUSTER = cn('relative z-10 flex items-center gap-1 sm:gap-1.5');

/** Compact metric pill on chips. */
export const HEADER_METRIC_PILL = cn(
  'inline-flex min-w-[1.1rem] items-center justify-center rounded-md',
  'px-1 py-0.5 text-[11px] font-bold tabular-nums leading-none',
);

/** Tone backgrounds for working-day stat tiles. */
export const WORKING_DAY_STAT_TONES = {
  visits: 'border-brand-200/80 bg-brand-50 text-brand-700',
  waiting: 'border-amber-200/80 bg-amber-50 text-amber-800',
  inChair: 'border-coral-200/80 bg-coral-50 text-coral-600',
  done: 'border-mint-100 bg-mint-100/80 text-mint-600',
} as const;

/** Interactive stat tile — matches other popover chips. */
export const WORKING_DAY_STAT_TILE = cn(
  'block rounded-xl border px-1.5 py-2 text-center shadow-soft transition',
  'hover:brightness-[0.98] hover:shadow-card',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
);

/** Segmented day switcher — compact pill track. */
export const WORKING_DAY_SEGMENT_TRACK = cn(
  'flex gap-0.5 rounded-lg bg-surface-100/80 p-0.5',
);

/** Day segment — inactive. */
export const WORKING_DAY_SEGMENT = cn(
  'inline-flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1',
  'text-[11px] font-semibold text-ink-500 transition',
  'hover:text-ink-700',
);

/** Day segment — active. */
export const WORKING_DAY_SEGMENT_ACTIVE = cn(
  WORKING_DAY_SEGMENT,
  'bg-white text-brand-700 shadow-soft ring-1 ring-surface-200/70',
);

/** Compact popover footer nav tile. */
export const WORKING_DAY_NAV_TILE = cn(
  'inline-flex flex-1 items-center justify-center gap-1 rounded-lg',
  'border border-surface-200/80 bg-white px-2 py-1.5',
  'text-[11px] font-semibold text-ink-700 shadow-soft transition',
  'hover:border-surface-200 hover:bg-surface-50 hover:text-ink-900',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
);

/** Tiny icon well for popover nav tiles. */
export const WORKING_DAY_NAV_ICON = cn(
  'flex size-5 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600',
);

/** Queue row / badge tones. */
export const QUEUE_TONE = {
  waiting: {
    row: cn(
      'rounded-lg border border-amber-200/80 border-l-[3px] border-l-amber-400',
      'bg-white hover:bg-amber-50/70',
    ),
    badge: 'bg-amber-100 text-amber-800',
    chip: 'bg-amber-100 text-amber-800',
    label: 'Waiting',
  },
  inChair: {
    row: cn(
      'rounded-lg border border-orange-200/80 border-l-[3px] border-l-orange-500',
      'bg-white hover:bg-orange-50/70',
    ),
    badge: 'bg-orange-100 text-orange-800',
    chip: 'bg-orange-100 text-orange-800',
    label: 'In chair',
  },
} as const;
